import { FirestoreCollections, Goal, ID, Task, Timestamp } from '@griply/types';

import { BatchDocuments, WriteBatchFn } from '../batch-documents';
import { detachWrite } from '../detach-write';
import { FirestoreInstance } from '../types/firestore';
import { stripUndefined } from '../utils/strip-undefined';

export type DuplicateGoalOptions<
  TDocRef = unknown,
  TCollectionRef = unknown,
  TFieldValue = unknown,
  TTimestamp = unknown,
  TDb = FirestoreInstance,
> = {
  goal: Goal;
  subGoals: Goal[];
  tasks: Task[];
  collection: (
    db: NoInfer<TDb>,
    path: string,
    ...rest: string[]
  ) => TCollectionRef;
  doc: (
    collectionRef: NoInfer<TCollectionRef>,
    ...rest: string[]
  ) => TDocRef & { id: ID };
  db: TDb;
  serverTimestamp: () => TFieldValue;
  timestampFromDate: (date: Date) => TTimestamp;
  writeBatch: WriteBatchFn<NoInfer<TDocRef>, NoInfer<TDb>>;
  onWriteError: (error: unknown) => void;
};

const remapIds = (
  ids: ID[] | null | undefined,
  oldIdToNewRef: Map<ID, { id: ID }>,
  { keepUnmapped }: { keepUnmapped: boolean },
): ID[] | null => {
  if (!ids) {
    return null;
  }
  const remapped = ids
    .map((id) => oldIdToNewRef.get(id)?.id ?? (keepUnmapped ? id : undefined))
    .filter((id): id is ID => !!id);
  return remapped.length ? remapped : null;
};

export const duplicateGoal = async <
  TDocRef,
  TCollectionRef,
  TFieldValue,
  TTimestamp,
  TDb = FirestoreInstance,
>(
  options: DuplicateGoalOptions<
    TDocRef,
    TCollectionRef,
    TFieldValue,
    TTimestamp,
    TDb
  >,
): Promise<ID> => {
  const {
    goal,
    subGoals,
    tasks,
    collection,
    doc,
    db,
    serverTimestamp,
    timestampFromDate,
    writeBatch,
    onWriteError,
  } = options;

  const goalCollection = collection(db, FirestoreCollections.Goals);
  const taskCollection = collection(db, FirestoreCollections.Tasks);

  const sortedSubGoals = subGoals
    .slice()
    .sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());

  const goalMap = new Map<ID, TDocRef & { id: ID }>();
  goalMap.set(goal.id, doc(goalCollection));
  sortedSubGoals.forEach(({ id }) => goalMap.set(id, doc(goalCollection)));

  const taskMap = new Map<ID, TDocRef & { id: ID }>();
  tasks.forEach((task) => taskMap.set(task.id, doc(taskCollection)));

  const batchDocuments = new BatchDocuments<TDocRef, TDb>(db, writeBatch);
  const dateBase = new Date();

  [goal, ...sortedSubGoals].forEach((goalToCopy, index) => {
    const docRef = goalMap.get(goalToCopy.id);
    if (!docRef) {
      return;
    }

    const newGoal: Goal = {
      ...goalToCopy,
      id: docRef.id,
      name:
        goal.id === goalToCopy.id
          ? `${goalToCopy.name} (copy)`
          : goalToCopy.name,
      image: null,
      parentIds: remapIds(goalToCopy.parentIds, goalMap, {
        keepUnmapped: true,
      }),
      goalSorting: remapIds(goalToCopy.goalSorting, goalMap, {
        keepUnmapped: false,
      }),
      taskSorting: remapIds(goalToCopy.taskSorting, taskMap, {
        keepUnmapped: false,
      }),
      sections:
        goalToCopy.sections?.map((goalSection) => ({
          ...goalSection,
          id: doc(goalCollection).id,
          tasks:
            remapIds(goalSection.tasks, taskMap, { keepUnmapped: false }) ?? [],
        })) ?? [],
      completedTaskCount: 0,
      excludedFromLimits: null,
      frozenAt: null,
      createdAt: timestampFromDate(
        new Date(dateBase.setMilliseconds(index)),
      ) as unknown as Date,
      updatedAt: serverTimestamp() as unknown as Timestamp,
      completedAt: null,
      roles: { owner: goalToCopy.roles.owner, all: [goalToCopy.roles.owner] },
    };

    batchDocuments.addDocument({ ref: docRef, data: stripUndefined(newGoal) });
  });

  tasks.forEach((task, index) => {
    const docRef = taskMap.get(task.id);
    if (!docRef) {
      return;
    }

    const newTask: Task = {
      ...task,
      id: docRef.id,
      lifeAreaId: null,
      goalId: task.goalId ? goalMap.get(task.goalId)?.id ?? null : null,
      childIds: remapIds(task.childIds, taskMap, { keepUnmapped: false }),
      parentIds: remapIds(task.parentIds, taskMap, { keepUnmapped: false }),
      subtaskOrder: remapIds(task.subtaskOrder, taskMap, {
        keepUnmapped: false,
      }),
      createdAt: timestampFromDate(
        new Date(dateBase.setMilliseconds(index)),
      ) as unknown as Timestamp,
      updatedAt: serverTimestamp() as unknown as Timestamp,
      completedAt: null,
      frozenAt: null,
      excludedFromLimits: null,
      roles: { owner: goal.roles.owner, all: [goal.roles.owner] },
    };

    batchDocuments.addDocument({ ref: docRef, data: stripUndefined(newTask) });
  });

  detachWrite(batchDocuments.commit(), onWriteError);

  const newRootRef = goalMap.get(goal.id);
  if (!newRootRef) {
    throw new Error('duplicateGoal: missing new root goal reference');
  }
  return newRootRef.id;
};
